import React, { useState, useMemo } from "react";
import {
  ChevronRight,
  ChevronDown,
  Folder as FolderIcon,
  FolderOpen,
  FolderPlus,
  Edit3,
  Trash2,
  Check,
  X,
  Calendar,
} from "lucide-react";
import type { Meeting } from "../hooks/useMeetingHistory";
import type { Folder } from "../services/folderService";
import { HistoryMeetingCard } from "./HistoryMeetingCard";

interface FolderTreeViewProps {
  folders: Folder[];
  meetings: Meeting[];
  loading: boolean;
  loadingMeetingId: string | number | null;
  movingMeetingId: string | number | null;
  isEn: boolean;
  onLoadMeeting: (meeting: Meeting) => void;
  onMoveMeeting: (meetingId: string | number, folderId: string | number | null) => Promise<void>;
  onUpdateTags: (meetingId: string | number, tags: string[]) => Promise<void>;
  onDeleteMeeting: (meetingId: string | number) => Promise<void>;
  onCreateFolder: (name: string, isEn: boolean) => Promise<string | number | null>;
  onRenameFolder: (id: string | number, name: string, isEn: boolean) => Promise<boolean>;
  onDeleteFolder: (id: string | number, isEn: boolean) => Promise<boolean>;
  formatDate: (isoString: string) => string;
  formatDuration: (meeting: Meeting) => string;
}

export function FolderTreeView({
  folders,
  meetings,
  loading,
  loadingMeetingId,
  movingMeetingId,
  isEn,
  onLoadMeeting,
  onMoveMeeting,
  onUpdateTags,
  onDeleteMeeting,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  formatDate,
  formatDuration,
}: FolderTreeViewProps) {
  // Track expanded folder IDs (default: "root" and all folder IDs expanded)
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({
    root: true,
  });

  // Track new folder creation input
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  // Track folder editing state
  const [editingFolderId, setEditingFolderId] = useState<string | number | null>(null);
  const [editFolderName, setEditFolderName] = useState("");

  // Track confirm delete meeting
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | number | null>(null);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  // Group meetings by folder_id
  const { rootMeetings, folderMeetingsMap } = useMemo(() => {
    const root: Meeting[] = [];
    const map: Record<string, Meeting[]> = {};

    // Initialize map for all existing folders
    folders.forEach((f) => {
      map[String(f._id)] = [];
    });

    meetings.forEach((m) => {
      if (m.folder_id !== null && m.folder_id !== undefined && m.folder_id !== "root") {
        const key = String(m.folder_id);
        if (map[key]) {
          map[key].push(m);
        } else {
          // Fallback to root if folder not found
          root.push(m);
        }
      } else {
        root.push(m);
      }
    });

    return { rootMeetings: root, folderMeetingsMap: map };
  }, [folders, meetings]);

  const toggleFolder = (idKey: string) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [idKey]: prev[idKey] === undefined ? false : !prev[idKey],
    }));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim() || isCreatingFolder) return;
    setIsCreatingFolder(true);
    try {
      const folderId = await onCreateFolder(newFolderName, isEn);
      if (folderId) {
        setNewFolderName("");
        setShowNewFolderInput(false);
        setExpandedFolders((prev) => ({ ...prev, [String(folderId)]: true }));
      }
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const [dragOverFolderId, setDragOverFolderId] = useState<string | number | "root" | null>(null);

  const handleRenameSubmit = async (folderId: string | number) => {
    if (!editFolderName.trim()) return;
    const success = await onRenameFolder(folderId, editFolderName, isEn);
    if (success) {
      setEditingFolderId(null);
    }
  };

  const handleDeleteClick = async (meetingId: string | number) => {
    setDeletingId(meetingId);
    await onDeleteMeeting(meetingId);
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  const handleFolderDrop = async (e: React.DragEvent, folderId: string | number | null) => {
    e.preventDefault();
    e.stopPropagation();

    let meetingId = e.dataTransfer.getData("text/plain");
    let sourceFolder: string | number | null = null;

    const rawJson = e.dataTransfer.getData("application/json");
    if (rawJson) {
      try {
        const parsed = JSON.parse(rawJson);
        meetingId = parsed.meetingId ?? meetingId;
        sourceFolder = parsed.sourceFolder ?? null;
      } catch {
        // fallback to text/plain
      }
    }

    if (!meetingId) return;
    setDragOverFolderId(null);

    const normalizedSource = sourceFolder === null || sourceFolder === undefined ? "root" : String(sourceFolder);
    const normalizedTarget = folderId === null ? "root" : String(folderId);
    if (normalizedSource === normalizedTarget) return;

    await onMoveMeeting(meetingId, folderId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent select-none">
      {/* ── CREATE NEW FOLDER HEADER ACTION ── */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-bold tracking-[0.1em] text-[var(--text-dim)] uppercase">
          {isEn ? "Explorer" : "Explorador de minutas"}
        </span>

        {showNewFolderInput ? (
          <form onSubmit={handleCreateSubmit} className="flex items-center gap-1">
            <input
              type="text"
              autoFocus
              value={newFolderName}
              onChange={(e) => {
                const val = e.target.value;
                setNewFolderName(val.charAt(0).toUpperCase() + val.slice(1));
              }}
              placeholder={isEn ? "Folder name..." : "Nueva..."}
              className="bg-[var(--bg3)] border border-[var(--b1)] rounded px-1.5 py-0.5 text-xs w-24 outline-none text-[var(--text)]"
            />
            <button
              type="submit"
              disabled={isCreatingFolder}
              className="text-accent text-xs font-bold hover:opacity-85 cursor-pointer px-1"
            >
              ✓
            </button>
            <button
              type="button"
              onClick={() => setShowNewFolderInput(false)}
              className="text-[var(--text-dim)] text-xs hover:text-[var(--text)] cursor-pointer px-1"
            >
              ✕
            </button>
          </form>
        ) : (
          <button
            onClick={() => setShowNewFolderInput(true)}
            className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline cursor-pointer"
          >
            <FolderPlus size={11} />
            <span>{isEn ? "+ Folder" : "+ Carpeta"}</span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
          <div className="w-6 h-6 border-2 border-[var(--accent)]/30 border-t-[var(--accent)] rounded-full animate-spin" />
          <p className="text-xs text-[var(--text-dim)] font-medium">
            {isEn ? "Loading history..." : "Obteniendo tus minutas..."}
          </p>
        </div>
      ) : meetings.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center p-4">
          <Calendar className="w-8 h-8 text-[var(--text-dim)] opacity-50 mb-3" />
          <p className="text-xs text-[var(--text-dim)] font-medium">
            {isEn ? "Record your first meeting to see it here." : "Grabá tu primera reunión para verla aquí."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* ── 1. ROOT / UNASSIGNED FOLDER NODE ── */}
          {rootMeetings.length > 0 && (
            <div
              className="group/node"
              onDragOver={handleDragOver}
              onDragEnter={() => setDragOverFolderId("root")}
              onDragLeave={() => setDragOverFolderId(null)}
              onDrop={(e) => handleFolderDrop(e, null)}
            >
              <div
                onClick={() => toggleFolder("root")}
                className={`flex items-center justify-between px-2 py-1 rounded-md hover:bg-[var(--bg3)] cursor-pointer transition-colors ${
                  dragOverFolderId === "root" ? "ring-2 ring-accent/50" : ""
                }`}
              >
                <div className="flex items-center gap-1.5 truncate">
                  {expandedFolders.root !== false ? (
                    <ChevronDown size={13} className="text-[var(--text-dim)] flex-shrink-0" />
                  ) : (
                    <ChevronRight size={13} className="text-[var(--text-dim)] flex-shrink-0" />
                  )}
                  {expandedFolders.root !== false ? (
                    <FolderOpen size={13} className="text-accent flex-shrink-0" />
                  ) : (
                    <FolderIcon size={13} className="text-accent flex-shrink-0" />
                  )}
                  <span className="text-xs font-semibold text-[var(--text)] truncate">
                    {isEn ? "Root (Unassigned)" : "Raíz (Sin carpeta)"}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium text-[var(--text-dim)] bg-[var(--pill)] border border-[var(--b1)] px-1.5 py-0.2 rounded-full">
                    {rootMeetings.length}
                  </span>
                  {dragOverFolderId === "root" && (
                    <div className="w-3 h-3 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
                  )}
                </div>
              </div>

              {/* Children meetings for Root */}
              {expandedFolders.root !== false && (
                <div className="pl-3 ml-3 border-l border-[var(--b1)] space-y-1.5 pt-1.5 pb-1">
                  {rootMeetings.map((meeting) => (
                    <div key={meeting._id} className="relative">
                      {deletingId === meeting._id && (
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] flex items-center justify-center gap-2 rounded-[8px] z-30 text-white select-none pointer-events-auto">
                          <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                          <span className="text-xs font-semibold text-red-200 tracking-wide">
                            {isEn ? "Deleting..." : "Borrando..."}
                          </span>
                        </div>
                      )}
                      <HistoryMeetingCard
                        meeting={meeting}
                        folders={folders}
                        isEn={isEn}
                        loadingMeetingId={loadingMeetingId}
                        movingMeetingId={movingMeetingId}
                        showFolderInfo={false}
                        onLoad={onLoadMeeting}
                        onMoveMeeting={onMoveMeeting}
                        onUpdateTags={onUpdateTags}
                        // onDeleteMeeting={handleDeleteClick}
                        confirmDeleteId={confirmDeleteId}
                        setConfirmDeleteId={setConfirmDeleteId}
                        deletingId={deletingId}
                        formatDate={formatDate}
                        formatDuration={formatDuration}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── 2. CUSTOM FOLDERS NODES ── */}
          {folders.map((folder) => {
            const folderKey = String(folder._id);
            const folderMeetings = folderMeetingsMap[folderKey] || [];
            const isExpanded = expandedFolders[folderKey] !== false;

            return (
              <div
              key={folder._id}
              className="group/node"
              onDragOver={handleDragOver}
              onDragEnter={() => setDragOverFolderId(folder._id)}
              onDragLeave={() => setDragOverFolderId(null)}
              onDrop={(e) => handleFolderDrop(e, folder._id)}
            >
                {/* Folder Header */}
                <div
                  className={`flex items-center justify-between px-2 py-1 rounded-md hover:bg-[var(--bg3)] cursor-pointer transition-colors group/folder ${
                    dragOverFolderId === folder._id ? "ring-2 ring-accent/50" : ""
                  }`}
                >
                  {editingFolderId === folder._id ? (
                    <div className="flex items-center gap-1.5 flex-1 pr-2" onClick={(e) => e.stopPropagation()}>
                      <FolderIcon size={13} className="text-accent flex-shrink-0" />
                      <input
                        type="text"
                        autoFocus
                        value={editFolderName}
                        onChange={(e) => setEditFolderName(e.target.value)}
                        onBlur={() => handleRenameSubmit(folder._id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRenameSubmit(folder._id);
                          if (e.key === "Escape") setEditingFolderId(null);
                        }}
                        className="bg-[var(--bg4)] text-xs font-medium border-b border-accent outline-none text-[var(--text)] w-full px-1"
                      />
                    </div>
                  ) : (
                    <div
                      onClick={() => toggleFolder(folderKey)}
                      className="flex items-center gap-1.5 truncate flex-1"
                    >
                      {isExpanded ? (
                        <ChevronDown size={13} className="text-[var(--text-dim)] flex-shrink-0" />
                      ) : (
                        <ChevronRight size={13} className="text-[var(--text-dim)] flex-shrink-0" />
                      )}
                      {isExpanded ? (
                        <FolderOpen size={13} className="text-accent flex-shrink-0" />
                      ) : (
                        <FolderIcon size={13} className="text-accent flex-shrink-0" />
                      )}
                      <span className="text-xs font-semibold text-[var(--text)] truncate">
                        {folder.name}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Rename/Delete folder action buttons on hover */}
                    {editingFolderId !== folder._id && (
                      <div className="opacity-0 group-hover/folder:opacity-100 flex items-center gap-1 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingFolderId(folder._id);
                            setEditFolderName(folder.name);
                          }}
                          className="p-0.5 hover:bg-black/10 dark:hover:bg-white/10 rounded text-[var(--text-dim)] hover:text-[var(--text)]"
                          title={isEn ? "Rename folder" : "Renombrar carpeta"}
                        >
                          <Edit3 size={11} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteFolder(folder._id, isEn);
                          }}
                          className="p-0.5 hover:bg-black/10 dark:hover:bg-white/10 rounded text-red-500 hover:text-red-600"
                          title={isEn ? "Delete folder" : "Eliminar carpeta"}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    )}

                    <span className="text-xs font-medium text-[var(--text-dim)] bg-[var(--pill)] border border-[var(--b1)] px-1.5 py-0.2 rounded-full">
                      {folderMeetings.length}
                    </span>
                    {dragOverFolderId === folder._id && (
                      <div className="w-3 h-3 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
                    )}
                  </div>
                </div>

                {/* Children meetings for this folder */}
                {isExpanded && (
                  <div className="pl-3 ml-3 border-l border-[var(--b1)] space-y-1.5 pt-1.5 pb-1">
                    {folderMeetings.length === 0 ? (
                      <p className="text-xs italic text-[var(--text-dim)] px-2 py-0.5">
                        {isEn ? "Empty folder" : "Carpeta vacía"}
                      </p>
                    ) : (
                      folderMeetings.map((meeting) => (
                        <div key={meeting._id} className="relative">
                          {deletingId === meeting._id && (
                            <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] flex items-center justify-center gap-2 rounded-[8px] z-30 text-white select-none pointer-events-auto">
                              <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                              <span className="text-xs font-semibold text-red-200 tracking-wide">
                                {isEn ? "Deleting..." : "Borrando..."}
                              </span>
                            </div>
                          )}
                          <HistoryMeetingCard
                            meeting={meeting}
                            folders={folders}
                            isEn={isEn}
                            loadingMeetingId={loadingMeetingId}
                            movingMeetingId={movingMeetingId}
                            showFolderInfo={false}
                            onLoad={onLoadMeeting}
                            onMoveMeeting={onMoveMeeting}
                            onUpdateTags={onUpdateTags}
                            // onDeleteMeeting={handleDeleteClick}
                            confirmDeleteId={confirmDeleteId}
                            setConfirmDeleteId={setConfirmDeleteId}
                            deletingId={deletingId}
                            formatDate={formatDate}
                            formatDuration={formatDuration}
                          />
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
